import { PropRuntimeGraph } from '../../../../../shared/types/PropRuntimeGraph';
import {
    PropNodeValue,
    PropRandomSource,
    RuntimePropInstance,
} from '../PropMechanicsTypes';

/**
 * 将运行时值规整为数组。
 *
 * @param value 可能是单值、数组、null 或 undefined。
 * @returns 数组形式；空值返回空数组。
 */
export function asArray<T>(value: T | T[] | null | undefined): T[] {
    if (value === null || value === undefined) return [];
    return Array.isArray(value) ? value : [value];
}

/**
 * 将未知值转换为数字。
 *
 * @param value 待转换值。
 * @param fallback 无法转换时使用的默认值。
 * @returns 可用于节点计算的数字。
 */
export function toNumber(value: unknown, fallback = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'boolean') return value ? 1 : 0;
    if (Array.isArray(value)) return value.length;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * 将节点输出值解释为布尔值。
 *
 * @param value 节点输出值。
 * @param fallback 空值时使用的默认值。
 * @returns 布尔结果；数组按是否有元素判断，数字按是否非 0 判断。
 */
export function toBoolean(value: PropNodeValue, fallback = false): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (Array.isArray(value)) return value.length > 0;
    if (value === null || value === undefined) return fallback;
    return Boolean(value);
}

/**
 * 判断对象是否为运行时道具实例。
 *
 * @param value 待判断对象。
 * @returns true 表示该对象具备运行时实例结构。
 */
export function isRuntimePropInstance(value: unknown): value is RuntimePropInstance {
    return Boolean(value && typeof value === 'object' && 'id' in value && 'graph' in value);
}

/**
 * 判断对象是否为道具运行时原型图。
 *
 * @param value 待判断对象。
 * @returns true 表示该对象具备 PropRuntimeGraph 结构。
 */
export function isPropRuntimeGraph(value: unknown): value is PropRuntimeGraph {
    return Boolean(value && typeof value === 'object' && '_id' in value && 'graph' in value);
}

/**
 * 判断实例是否为数据库中“空位置”原型创建出的盘面占位。
 *
 * @param item 运行时实例。
 * @returns true 表示该实例代表空格。
 */
export function isEmptyPropInstance(item: RuntimePropInstance | undefined): boolean {
    return Boolean(item && item.graph.name === '空位置');
}

/**
 * 按实例 id 去重，同时过滤死亡实例和默认空位置实例。
 *
 * @param items 候选实例。
 * @param includeEmpty 是否保留“空位置”实例。
 * @returns 去重后的存活实例列表。
 */
export function uniqueLiveItems(items: RuntimePropInstance[], includeEmpty = false): RuntimePropInstance[] {
    const seen = new Set<string>();
    const result: RuntimePropInstance[] = [];
    for (const item of items) {
        if (!item || !item.alive) continue;
        if (!includeEmpty && isEmptyPropInstance(item)) continue;
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        result.push(item);
    }
    return result;
}

/**
 * 读取道具当前可用于倍率计算的魔力值。
 *
 * @param item 运行时实例。
 * @returns 基础产出、永久成长、临时加成和永久倍率共同作用后的魔力值。
 */
export function getRuntimeYield(item: RuntimePropInstance): number {
    return Number(item.graph.yield || 0) + Number(item.grow || 0);
}

/**
 * 从数组中随机抽取最多 count 个元素。
 *
 * @param items 源数组。
 * @param count 抽取数量。
 * @param rng 随机源。
 * @returns 抽取结果；不会修改原数组。
 */
export function pickRandomItems<T>(items: T[], count: number, rng: PropRandomSource): T[] {
    const amount = Math.max(0, Math.floor(count));
    if (amount <= 0 || items.length <= 0) return [];
    const pool = [...items];
    const result: T[] = [];
    while (pool.length > 0 && result.length < amount) {
        const index = rng.int(0, pool.length);
        result.push(pool.splice(index, 1)[0]);
    }
    return result;
}
